import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  RepositoryCollaborator,
  RepositoryInvite,
  RepositoryInvitePost,
  RepositoryStatus,
} from '@types'
import { ACCESS_TYPE_OWNER } from '@/const'

import {
  addStoreListItem,
  prepareResponseError,
  removeStoreListItem,
  updateStoreListItem,
} from '../utils/helpers'
import api from '../api'
import { AxiosResponse } from 'axios'

export const useRepositoriesCollaboratorsStore = defineStore(
  'repositories-collaborators',
  () => {
    // INIT

    const list = ref<(RepositoryInvite | RepositoryCollaborator)[]>([])
    const invitesList = ref<RepositoryInvite[]>([])

    const loading = ref(false)
    const loadingAction = ref(false)
    const loadingInvites = ref(false)

    let abortController = new AbortController()

    // GETTERS

    const getList = computed(() =>
      list.value.map(collaborator => ({
        ...collaborator,
        ...(collaborator.access_type === ACCESS_TYPE_OWNER
          ? { status: RepositoryStatus.OWNER }
          : {}),
      })),
    )

    const getInvites = computed(() => invitesList.value)

    // SETTERS

    const setList = (data: (RepositoryInvite | RepositoryCollaborator)[]) => {
      list.value = data
    }

    const setInvites = (data: RepositoryInvite[]) => {
      invitesList.value = data
    }

    // ACTIONS

    const fetch = async (id: string): Promise<void> => {
      loading.value = true
      try {
        const result = await api.get<RepositoryCollaborator[]>(
          `collaborators?repository_id=${id}`,
          { signal: abortController.signal },
        )
        setList(result.data)
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loading.value = false
      }
    }

    const fetchInvites = async () => {
      loadingInvites.value = true
      try {
        const result = await api.get<RepositoryInvite[]>('invites')
        setInvites(result.data)
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingInvites.value = false
      }
    }

    const update = async (
      collaboratorId: string,
      repositoryId: string,
      data: Partial<RepositoryCollaborator>,
    ): Promise<void> => {
      try {
        const response = await api.put<RepositoryCollaborator>(
          `collaborators/${collaboratorId}?repository_id=${repositoryId}`,
          data,
        )
        updateStoreListItem<any>(list.value, response.data, 'email')
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const destroy = async (
      email: string,
      collaboratorId: string,
      repositoryId: string,
    ): Promise<void> => {
      loadingAction.value = true
      try {
        await api.delete(
          `collaborators/${collaboratorId}?repository_id=${repositoryId}`,
        )
        removeStoreListItem<any>(list.value, email, 'email')
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingAction.value = false
      }
    }

    const addInvite = async (params: RepositoryInvitePost) => {
      loadingAction.value = true
      try {
        const result = await api.post<
          RepositoryInvite,
          AxiosResponse<RepositoryInvite>,
          RepositoryInvitePost
        >('invites', params)
        const data = result.data
        const collaborator = list.value.find(item => item.email === data.email)
        if (collaborator) {
          updateStoreListItem<any>(
            list.value,
            { status: RepositoryStatus.PENDING },
            'email',
          )
        } else {
          addStoreListItem<any>(
            list.value,
            {
              ...data,
              user: { email: data.email },
            },
            'email',
          )
        }
        return result.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingAction.value = false
      }
    }

    const updateInvite = (id: string, accepted: boolean) => {
      const invite = invitesList.value.find(item => item.id === id)
      if (!invite) return false
      updateStoreListItem<RepositoryInvite>(invitesList.value, {
        ...invite,
        accepted,
      })
      return true
    }

    const acceptInvite = async (id: string): Promise<void> => {
      const result = updateInvite(id, true)
      if (!result) return
      try {
        await api.put(`invites/${id}/accept`)
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const declineInvite = async (id: string): Promise<void> => {
      const result = updateInvite(id, false)
      if (!result) return
      try {
        await api.put(`invites/${id}/decline`)
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const removeInvite = async (id: string): Promise<void> => {
      loadingAction.value = true
      try {
        await api.delete(`invites/${id}`)
        removeStoreListItem<any>(list.value, id)
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loadingAction.value = false
      }
    }

    const cancel = () => {
      abortController.abort()
      abortController = new AbortController()
    }

    const clear = () => {
      loading.value = true
      loadingAction.value = false
      loadingInvites.value = true
      setList([])
      setInvites([])
    }

    return {
      loading,
      loadingAction,
      loadingInvites,

      getList,
      getInvites,

      fetch,
      fetchInvites,

      update,
      destroy,

      addInvite,
      updateInvite,
      acceptInvite,
      declineInvite,
      removeInvite,

      cancel,
      clear,
    }
  },
)
